import { useEffect } from 'react';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import type { IrremovableFieldsConfig } from '@jira/jql-builder__basic/src/utils/irremovable-fields/index.tsx';
import { isHttpClientErrorResponse } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouterContext,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import {
	DEFAULT_CALENDAR_FILTER_FIELDS,
	RESOURCE_TYPE_FILTER_FIELD,
} from './resourceTypeName.constants.tsx';
import { QUERY } from './Query.tsx';
import type { RequestTypeFieldResponse } from './RequestTypeFieldResponse.types.tsx';
import { reportError, TARGET_FILTER_TYPE as REQUEST_TYPE_FILTER_TYPE } from './reportError.tsx';

const fetchFilterFields = async (cloudId: string): Promise<IrremovableFieldsConfig> => {
	try {
		const requestTypeFieldResponse = await performPostRequest<RequestTypeFieldResponse>(
			'/gateway/api/graphql',
			{
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
					},
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		const requestTypeField = requestTypeFieldResponse.data?.jira?.requestTypeField;
		if (!requestTypeField) {
			return DEFAULT_CALENDAR_FILTER_FIELDS;
		}

		return [
			{
				...requestTypeField,
				searchTemplate: requestTypeField.searchTemplate.key,
				fieldType: requestTypeField.type,
			},
			...DEFAULT_CALENDAR_FILTER_FIELDS,
		];
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (!isHttpClientErrorResponse(error)) {
			reportError(error);
		}

		return DEFAULT_CALENDAR_FILTER_FIELDS;
	}
};

const filterFieldsResource = createResource({
	type: RESOURCE_TYPE_FILTER_FIELD,
	getKey: ({ match, query }: RouterContext) => {
		const { projectKey, projectId } = getProjectKeyId(match, query);
		const projectIdOrKey = projectKey || projectId;
		return RESOURCE_TYPE_FILTER_FIELD + projectIdOrKey;
	},
	getData: (_, { tenantContext: { cloudId } }) => fetchFilterFields(cloudId),
	maxAge: Infinity,
});

export const getCalendarFilterFieldsResource = (): RouteResource<IrremovableFieldsConfig> =>
	filterFieldsResource;
export const useCalendarFilterResource = ({
	isSimplified,
}: {
	isSimplified: boolean;
}): UseResourceHookResponse<IrremovableFieldsConfig> => {
	const resource = useResource(filterFieldsResource);

	useEffect(() => {
		if (
			!isSimplified &&
			!resource.loading &&
			!resource.error &&
			!resource.data?.find(
				(field) =>
					typeof field === 'object' && Object.values(field).includes(REQUEST_TYPE_FILTER_TYPE),
			)
		) {
			reportError(new Error('Unable to prefetch request type'));
		}
	}, [isSimplified, resource.data, resource.error, resource.loading]);

	return resource;
};
